<template>
  <div class="grid grid-rows my-4 recent-order-history">
    <div class="md:flex md:items-center md:justify-between">
      <div class="min-w-0 flex mb-2">
        <h2 class="text-2xl font-bold text-gray-900 sm:truncate sm:tracking-tight">Eerder bestelde producten</h2>
      </div>
    </div>
    <div class="relative">
      <div class="grid grid-rows my-2" v-if="recentOrderHistory.length === 0">
        <Alert
          :html="true"
          htmlMessage="Er zijn geen recente bestellingen geplaatst voor deze patiënt."
          type="info"
        ></Alert>
      </div>
      <div v-else class="grid sm:grid-rows-4 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        <card
          v-for="item in visibleCards"
          :key="item.id"
          class="border border-disabled-background h-full flex flex-col"
          :class="[item.AlternativeProductModel === null && item.ProductStatus === 3 ? 'bg-primary-product' : '']"
        >
          <template v-slot>
            <div class="flex flex-col flex-grow">
              <div
                class="min-h-4 -mt-4 -ml-2 flex flex-wrap items-center gap-1"
                v-if="item.showLabel || item.IsNewPackage || item.IsMediqSelect"
              >
                <span
                  v-if="item.showLabel"
                  class="relative rounded px-1 text-xs text-white bg-primary-default border border-primary-default font-semibold"
                >
                  Laatst besteld
                </span>
                <product-flag-labels :is-new-package="item.IsNewPackage" :is-mediq-select="item.IsMediqSelect" />
              </div>
              <div v-if="item.AlternativeProductModel !== null && item.AlternativeProductModel[0].IsFollowup">
                <Alert :html="true" type="info" :htmlMessage="item.OriginalProductMessage"></Alert>
              </div>
              <div class="flex items-center justify-center my-2 h-48 w-full flex-shrink-0">
                <img
                  :src="getImageUrl(item.ImageUrl)"
                  @error="handleImageError"
                  class="mx-auto max-h-full max-w-full"
                  :alt="item.Name ? `Image of ${item.Name}` : 'Product image'"
                />
              </div>
              <div
                class="text-tdefault-color font-semibold text-base mb-1 cursor-pointer h-12 overflow-hidden"
                @click.prevent="
                  goToProductDetailPage(
                    item.BaseProductId,
                    item.AlternativeProductModel === null && item.ProductStatus === 3
                  )
                "
              >
                {{ $toTitleCase(item.Name) }}
              </div>
              <div class="text-tdefault-color text-sm mb-2">Artikelcode: {{ item.ProductCode }}</div>
              <div class="text-talternative-color text-sm">Besteld op {{ $formatDate(item.OrderDate) }}</div>
              <div class="text-talternative-color text-sm mb-4">Besteld door: {{ item.OrderBy }}</div>
            </div>
            <div class="mt-auto">
              <div class="grid grid-cols-2 gap-2 mb-2">
                <div class="flex items-center hidden">
                  <span
                    class="inline-flex items-center gap-1 border-b border-dashed px-1"
                    :class="item.Orderable ? '' : 'text-warning-default border-warning-default'"
                  >
                    <component :is="item.Orderable ? '' : 'XCircleIcon'" class="h-5 w-5" />
                    {{ item.Orderable ? '' : 'Niet leverbaar' }}
                  </span>
                </div>
                <div class="flex items-center">
                  <span
                    class="inline-flex items-center gap-1 border-b border-dashed px-1"
                    :class="
                      item.Insured
                        ? 'text-success-default border-success-default'
                        : 'text-danger-text border-danger-text'
                    "
                  >
                    <component :is="item.Insured ? 'CheckCircleIcon' : 'ExclamationCircleIcon'" class="h-5 w-5" />
                    {{ item.Insured ? 'Vergoed' : 'Niet vergoed' }}
                  </span>
                </div>
              </div>

              <div v-if="!patientGegevens.IsDeceased && !patientGegevens.IsArchived">
                <button
                  v-if="item.AlternativeProductModel !== null && item.AlternativeProductModel[0].IsAlternative"
                  class="flex justify-center text-white text-xs md:text-base font-medium items-center bg-primary-default border border-primary-default rounded-full p-2"
                  @click.prevent="showAlternateProducts(item.AlternativeProductModel, 1)"
                >
                  Bekijk alternatieve product
                </button>
                <button
                  v-else-if="item.AlternativeProductModel !== null && item.AlternativeProductModel[0].IsSubstitute"
                  class="flex justify-center text-white text-xs md:text-base font-medium items-center bg-primary-default border border-primary-default rounded-full p-2"
                  @click.prevent="showAlternateProducts(item.AlternativeProductModel, 2)"
                >
                  Bekijk vervangend product
                </button>
                <button
                  v-else-if="isOrderable(item)"
                  class="flex justify-center text-xs md:text-base font-medium items-center bg-primary-default border border-primary-default rounded-full p-2"
                  @click.prevent="order(item.ProductId, count, item.IsConsignmentProduct)"
                  :aria-label="`Add ${$toTitleCase(item.Name) || 'product'} to cart`"
                >
                  <add-shopping-cart class="h-5 w-5 inline-block" aria-hidden="true" />
                </button>
                <span
                  v-else-if="String(item.MaterialStatusCode) === '30'"
                  class="text-sm font-semibold text-primary-default"
                >
                  Op bestelling verkrijgbaar
                </span>
                <span v-else class="text-sm font-semibold text-primary-default">Niet bestelbaar</span>
              </div>
            </div>
          </template>
        </card>
      </div>
      <div class="absolute w-full top-48 z-49" v-if="recentOrderHistory.length != 0 && visibleCards.length > 0">
        <div
          class="flex mb-4"
          :class="[(showNext && showPrev) || (showPrev && !showNext) ? 'justify-between items-center' : 'justify-end']"
        >
          <button
            v-if="showPrev"
            @click="prev"
            :class="{ hidden: currentSlideIndex === 0 }"
            class="rounded-full bg-primary-default border border-primary-default -ml-4"
          >
            <ChevronLeftIcon class="h-8 w-8 inline-block text-white" aria-hidden="true" />
          </button>
          <button
            v-if="showNext"
            @click="next"
            :class="{ hidden: currentSlideIndex + itemsPerPage >= recentOrder.length }"
            class="rounded-full bg-primary-default border border-primary-default -mr-4"
          >
            <ChevronRightIcon class="h-8 w-3 w-8 inline-block text-white" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        class="my-4 text-primary-default flex cursor-pointer text-sm md:text-base lg:text-base"
        @click.prevent="goToCatalog"
        v-if="recentOrderHistory.length != 0 && visibleCards.length > 0"
      >
        Bekijk alle eerder bestelde producten
        <ArrowRightIcon class="h-5 w-5 flex-shrink-0 ml-2 text-primary-default" aria-hidden="true" />
      </div>
    </div>
    <!--Pop up to show order success with navigation buttons -->
    <generic-modal v-if="showPopUp" :has-close-button="true" @close="closeModal">
      <template #header> Product is toegevoegd aan bestellijst. </template>
      <template #body>
        <p>
          Wilt u naar de catalogus om meer producten toe te voegen? Of ga naar de checkout om uw bestelling te
          versturen.
        </p>
        <v-warning-message
          v-if="!getProductInsuredStatus()"
          class="warning-message mt-2"
          warning="Let op!"
          message="Dit product wordt niet vergoed."
        />
      </template>
      <template #footer>
        <v-button tagelement="a" hollow md transparent @click.prevent="$router.push('/catalog')">
          Ga naar catalogus
        </v-button>
        <v-button tagelement="a" md primary @click.prevent="$router.push('/bestellijst')"> Ga naar checkout </v-button>
      </template>
    </generic-modal>
    <!--Pop up to show alternative products for order -->
    <generic-modal
      v-if="showAlternateProductPopUp"
      :has-close-button="true"
      :modal-Medium-Width="true"
      @close="closeAlternateProductModal"
    >
      <template #header>
        <div v-if="productRelationType == 1" class="mb-4">Alternatief product</div>
        <div v-else-if="productRelationType == 2" class="mb-4">Vervangend product</div>
      </template>
      <template #body>
        <div class="mb-4">
          <Alert :html="true" :htmlMessage="alternativeProductHtmlMsg" type="info"></Alert>
        </div>
        <div class="relative mb-4">
          <div class="grid sm:grid-rows-4 md:grid-cols-2 lg:grid-cols-2 gap-2 mb-4">
            <card
              v-for="item in alternateCards"
              :key="item.id"
              class="border border-disabled-background h-full flex flex-col"
              :class="[item.IsOriginalProduct ? 'bg-primary-product' : '']"
            >
              <template v-slot>
                <div class="flex flex-col flex-grow -mt-6">
                  <div class="flex items-center justify-center my-2 h-28 w-full flex-shrink-0">
                    <img
                      :src="getImageUrl(item.ImageUrl)"
                      @error="handleImageError"
                      class="mx-auto max-h-full max-w-full"
                      :alt="item.Name ? `Image of ${item.Name}` : 'Product image'"
                    />
                  </div>
                  <div
                    class="text-tdefault-color font-semibold text-xs mb-1 cursor-pointer h-14 overflow-hidden"
                    @click.prevent="goToProductDetailPage(item.BaseProductId, item.IsOriginalProduct)"
                  >
                    {{ $toTitleCase(item.Name) }}
                  </div>
                  <div class="text-talternative-color text-sm mb-4">{{ item.PackagingDetails }}</div>
                </div>
                <div class="mt-auto">
                  <div class="grid grid-cols-2 gap-2 mb-2">
                    <div class="flex items-center">
                      <span
                        class="inline-flex items-center gap-1 border-b border-dashed px-1"
                        :class="item.Orderable ? '' : 'text-warning-default border-warning-default'"
                      >
                        <component :is="item.Orderable ? '' : 'XCircleIcon'" class="h-5 w-5" />
                        {{ item.Orderable ? '' : 'Niet leverbaar' }}
                      </span>
                    </div>
                    <div class="flex items-center">
                      <span
                        class="inline-flex items-center gap-1 border-b border-dashed px-1"
                        :class="
                          item.Insured
                            ? 'text-success-default border-success-default'
                            : 'text-danger-text border-danger-text'
                        "
                      >
                        <component :is="item.Insured ? 'CheckCircleIcon' : 'ExclamationCircleIcon'" class="h-5 w-5" />
                        {{ item.Insured ? 'Vergoed' : 'Niet vergoed' }}
                      </span>
                    </div>
                  </div>

                  <div
                    class="-mb-4"
                    v-if="
                      !patientGegevens.IsDeceased &&
                      !patientGegevens.IsArchived &&
                      !item.IsOriginalProduct &&
                      isOrderable(item)
                    "
                  >
                    <table>
                      <tbody>
                        <tr>
                          <td class="pt-3 pb-2 capitalize whitespace-normal align-middle">
                            <div class="border border-gray-400 rounded-md h-8">
                              <v-minus
                                class="qty-minus float-left border-r border-gray-400 cursor-pointer"
                                @click.prevent="reduceQuantity(item)"
                              />
                              <input
                                class="max-w-10 float-left h-7 font-bold pl-4 pt-1 align-middle"
                                v-model="currentQuantity"
                                type="number"
                                min="1"
                                max="999"
                                maxlength="3"
                                autocomplete="off"
                                @change="saveProductQuantity(currentQuantity, item)"
                              />
                              <v-plus
                                class="qty-plus float-left text-right border-l border-gray-400 cursor-pointer"
                                @click.prevent="increaseQuantity(item)"
                              />
                            </div>
                          </td>
                          <td class="align-middle pl-10">
                            <button
                              class="flex justify-center pl-5 pr-5 text-white text-xs md:text-base font-medium items-center bg-primary-default border border-primary-default rounded-full p-2"
                              @click.prevent="order(item.ProductId, item.Quantity, item.IsConsignmentProduct)"
                            >
                              Bestellen
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </card>
          </div>
          <div
            class="absolute w-full top-48 z-50"
            v-if="recentAlternateProducts.length != 0 && alternateCards.length > 0"
          >
            <div
              class="flex mb-4"
              :class="[
                (showAlternateNext && showAlternatePrev) || (showAlternatePrev && !showAlternateNext)
                  ? 'justify-between items-center'
                  : 'justify-end'
              ]"
            >
              <button
                v-if="showAlternatePrev"
                @click="alternatePrev"
                :class="{ hidden: alternateSlideIndex === 0 }"
                class="rounded-full bg-primary-default border border-primary-default -ml-4"
              >
                <ChevronLeftIcon class="h-8 w-8 inline-block text-white" aria-hidden="true" />
              </button>
              <button
                v-if="showAlternateNext"
                @click="alternateNext"
                :class="{ hidden: alternateSlideIndex + alternatesPerPage >= recentAlternateProducts.length }"
                class="rounded-full bg-primary-default border border-primary-default -mr-4"
              >
                <ChevronRightIcon class="h-8 w-3 w-8 inline-block text-white" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </template>
      <template #footer> </template>
    </generic-modal>
  </div>
</template>
<script>
import router from '@/router';
import { useStore } from 'vuex';
import { computed, ref, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import VWarningMessage from '@/components/shared/WarningMessage';
import GenericModal from '@/components/shared/GenericModal';
import { shoppingCart$, addToShoppingCart, activePpwRecId$ } from '@/services';
import { ArrowRightIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/vue/20/solid';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline';
import AddShoppingCart from 'icons/CartPlus';
import VPlus from 'icons/Plus';
import VMinus from 'icons/Minus';
import ProductFlagLabels from '@/components/shared/ProductFlagLabels';

export default {
  name: 'RecentOrderedProducts',
  components: {
    VWarningMessage,
    GenericModal,
    ArrowRightIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon,
    AddShoppingCart,
    ChevronRightIcon,
    ChevronLeftIcon,
    VPlus,
    VMinus,
    ProductFlagLabels
  },
  props: {
    type: Boolean,
    image: String,
    title: String,
    description: String,
    productId: String,
    sapId: String,
    shortDescription: String,
    productUnit: String,
    baseProduct: Boolean,
    patientId: Number,
    isActiveLicense: {
      default: false,
      type: Boolean
    }
  },
  setup(props) {
    const store = useStore();
    const storeSiteCode = store.state.navigation.siteCode;
    const shoppingCart = ref(null);
    const activePpwRecId = ref(null);
    const fetchRecentOrderHistory = () => store.dispatch('patientOrderHistory/getRecentOrderHistory');
    activePpwRecId$.subscribe((value) => (activePpwRecId.value = value));
    shoppingCart$.subscribe((value) => (shoppingCart.value = value));

    const recentOrderHistory = computed(() => store.getters['patientOrderHistory/getRecentOrderHistory']);
    const resetRecentOrderHistory = () => store.commit('patientOrderHistory/setRecentOrderHistory', []);
    const patientGegevens = computed(() => store.getters['patient/getPatientGegevens']);
    const isInfusion = computed(() => {
      return storeSiteCode.value === 'MIF';
    });
    const recentOrder = computed(() => {
      const computedOrders = Array.isArray(recentOrderHistory.value) ? recentOrderHistory.value : [];
      const firstOrder = computedOrders[0]?.OrderId;

      const result = computedOrders.map((item) => ({
        ...item,
        showLabel: item.OrderId === firstOrder
      }));

      // Slice if filterCount is present
      return result;
    });

    const currentSlideIndex = ref(0);
    const itemsPerPage = ref(4);
    const resetCurrentIndex = () => {
      currentSlideIndex.value = 0;
    };
    const totalPages = computed(() => {
      return Math.ceil(recentOrder.value.length / itemsPerPage.value);
    });

    const visibleCards = computed(() => {
      let start = currentSlideIndex.value * itemsPerPage.value;
      let end = start + itemsPerPage.value;
      if (start > recentOrder?.value.length) {
        resetCurrentIndex();
        start = 0;
        end = itemsPerPage.value;
      }
      return recentOrder.value ? recentOrder.value.slice(start, end) : [];
    });

    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width >= 1024) itemsPerPage.value = 4;
      else if (width >= 768) itemsPerPage.value = 2;
      else itemsPerPage.value = 1;
    };

    const showPrev = computed(() => currentSlideIndex.value > 0);
    const showNext = computed(() => currentSlideIndex.value < totalPages.value - 1);

    const next = () => {
      if (currentSlideIndex.value < totalPages.value - 1) {
        currentSlideIndex.value++;
      }
    };

    const prev = () => {
      if (currentSlideIndex.value > 0) {
        currentSlideIndex.value--;
      }
    };

    const isOrderable = (item) => {
      return item.Orderable && !(isInfusion.value && item.ProductId?.startsWith('17')) && props.isActiveLicense;
    };

    onMounted(() => {
      fetchRecentOrderHistory();
      updateItemsPerPage();
      window.addEventListener('resize', updateItemsPerPage);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateItemsPerPage);
      resetRecentOrderHistory();
    });

    return {
      fetchRecentOrderHistory,
      storeSiteCode,
      recentOrderHistory,
      patientGegevens,
      activePpwRecId,
      shoppingCart,
      recentOrder,
      next,
      prev,
      visibleCards,
      updateItemsPerPage,
      itemsPerPage,
      currentSlideIndex,
      isOrderable,
      totalPages,
      showNext,
      showPrev
    };
  },
  data() {
    return {
      count: 1,
      showPopUp: false,
      showAlternateProductPopUp: false,
      clickedProductId: null,
      recentAlternateProducts: null,
      alternateCards: null,
      alternatesPerPage: 2,
      alternateSlideIndex: 0,
      alternatePages: 0,
      showAlternatePrev: false,
      showAlternateNext: false,
      currentQuantity: 1,
      productRelationType: 0,
      alternativeProductHtmlMsg:
        '<b>Onze product specialisten raden dit alternatieve product aan </b> </br> Dit product is niet te bestellen. Onze product specialisten hebben voor u een alternatief product geselecteerd.'
    };
  },
  computed: {
    /**
     * @returns {array} - list with orders filtered by dynamic value of count and sliced by dynamic value of filtercount
     */
  },
  methods: {
    goToCatalog() {
      // Before routing, set meta value to true
      router.getRoutes().forEach((route) => {
        if (route.name === 'catalog') {
          route.meta.recentOrders = true;
        }
      });
      router.push('/catalog');
    },
    closeModal() {
      this.showPopUp = false;
    },
    closeAlternateProductModal() {
      this.showAlternateProductPopUp = false;
      this.currentQuantity = 1;
      this.alternateSlideIndex = 0;
    },
    showAlternateProducts(alternativeProductModel, type) {
      this.productRelationType = type;

      if (type === 2) {
        this.alternativeProductHtmlMsg = this.alternativeProductHtmlMsg.replaceAll('alternatieve', 'vervangende');
        this.alternativeProductHtmlMsg = this.alternativeProductHtmlMsg.replaceAll('alternatief', 'vervangend');
      }

      this.recentAlternateProducts = computed(() => {
        const computedAlternateProducts = Array.isArray(alternativeProductModel) ? alternativeProductModel : [];
        const firstProduct = computedAlternateProducts[0]?.ProductId;

        const result = computedAlternateProducts.map((item) => ({
          ...item,
          showLabel: item.ProductId === firstProduct
        }));
        // Slice if filterCount is present
        return result;
      });

      this.alternatePages = computed(() => {
        return Math.ceil(this.recentAlternateProducts.length / this.alternatesPerPage);
      });

      this.alternateCards = computed(() => {
        let start = this.alternateSlideIndex * this.alternatesPerPage;
        let end = start + this.alternatesPerPage;
        if (start > this.recentAlternateProducts?.length) {
          this.resetAlternateIndex();
          start = 0;
          end = 2;
        }
        return this.recentAlternateProducts ? this.recentAlternateProducts.slice(start, end) : [];
      });

      this.showAlternatePrev = computed(() => this.alternateSlideIndex > 0);
      this.showAlternateNext = computed(() => this.alternateSlideIndex < this.alternatePages - 1);

      this.showAlternateProductPopUp = true;
    },
    updateAlternateItemsPerPage() {
      const width = window.innerWidth;
      if (width >= 1024) this.alternatesPerPage = 2;
      else if (width >= 768) this.alternatesPerPage = 2;
      else this.alternatesPerPage = 1;
    },
    alternateNext() {
      if (this.alternateSlideIndex < this.alternatePages - 1) {
        this.alternateSlideIndex++;
      }
    },
    alternatePrev() {
      if (this.alternateSlideIndex > 0) {
        this.alternateSlideIndex--;
      }
    },
    resetAlternateIndex() {
      this.alternateSlideIndex = 0;
    },
    order(ProductId, count, IsConsignmentProduct) {
      let order = this.recentOrderHistory;
      if (order) {
        this.closeAlternateProductModal();
        this.clickedProductId = ProductId;
        order.Insured = this.getProductInsuredStatus();
        order.addedToBasket = true;
        order.count = this.count;
        addToShoppingCart(this.shoppingCart, ProductId, count, this.activePpwRecId, IsConsignmentProduct);

        this.showPopUp = true;
      }
    },
    getProductInsuredStatus() {
      return (
        this.recentOrderHistory.filter((x) => x.ProductId === this.clickedProductId && x.Insured === true).length > 0
      );
    },
    goToProductDetailPage(baseProductId, flag) {
      router.push('/catalog/baseProduct/' + baseProductId);
    },
    reduceQuantity(item) {
      if (item.Quantity === 1) {
        return;
      }
      this.saveProductQuantity(item.Quantity - 1, item);
    },
    increaseQuantity(item) {
      if (item.Quantity === 999) {
        return;
      }
      this.saveProductQuantity(item.Quantity + 1, item);
    },
    saveProductQuantity(inputValue, item) {
      let regex = /^[0-9]{1,3}$/;
      let quantity = parseInt(inputValue, 10);
      if (quantity <= 0) {
        quantity = 1;
      }
      if (quantity > 999) {
        quantity = 999;
      }
      if (!regex.test(quantity) || !quantity || quantity <= 0) {
        item.Quantity = 0;
        return;
      }
      item.Quantity = quantity;
      this.currentQuantity = quantity;
    }
  }
};
</script>
<style lang="scss" scoped>
.bg-primary-product {
  background-color: #dee2e6 !important;
}
.generic-model-width {
  width: 700px !important;
}
.qty-minus {
  fill: $teal-darkest !important;
  height: 31px;
}
.qty-plus {
  fill: $teal-darkest !important;
  height: 31px;
}
</style>
